import React from 'react'
import { FaTimes as X } from 'react-icons/fa'
import { MidiRouter } from '@/components/midi/MidiRouter'
import { MappingList } from '@/components/midi/MappingList'
import { useMidiStore } from '@/stores/midiStore'
import { midiNoteName, type Project } from '@/stores/sequencerStore'
import type { ResolvedPad } from '@/lib/resolvePad'

interface SequencerMidiPanelProps {
  /**
   * The full 16-pad resolution for the active project — used to
   * label the controller-mapping rows with each pad's kit/sample
   * name so the user knows which bind they're editing.
   */
  resolvedPads: Array<ResolvedPad | null>
  project: Project
  midiLearnArmedPad: number | null
  onArmPad: (padIndex: number | null) => void
  onUpgradeNeeded: () => void
  onClose: () => void
}

/**
 * In-page MIDI control surface for the sequencer. Three sections
 * stacked vertically:
 *
 *  1. The shared `MidiRouter` patchbay — same component /midi uses,
 *     just embedded here so users can wire up hardware without
 *     changing route.
 *  2. Controller mapping — MIDI Learn per kit pad. Click Learn on
 *     a row, press a key on your controller, the note is bound to
 *     that pad. Solves the SP-404 case where the controller's
 *     pad-note layout doesn't match the kit's GM-drum defaults.
 *  3. (Overflow MIDI pads are edited in PadEditor — not duplicated
 *     here to keep the surface focused on controller wiring.)
 */
export const SequencerMidiPanel: React.FC<SequencerMidiPanelProps> = ({
  resolvedPads,
  project,
  midiLearnArmedPad,
  onArmPad,
  onUpgradeNeeded,
  onClose,
}) => {
  const padNoteBindings = useMidiStore(s => s.padNoteBindings)
  const clearPadNote = useMidiStore(s => s.clearPadNote)
  const clearAllPadBindings = useMidiStore(s => s.clearAllPadBindings)

  return (
    // Cap the panel at 80% of viewport height and let the body scroll
    // internally — the patchbay + controller mapping + MappingList
    // together routinely exceed the viewport's natural budget, and
    // the enclosing page column is already shared with the editor,
    // so without this constraint the panel pushes everything else
    // off-screen.
    <section
      className="rounded-xl border border-brd-subtle bg-surface-sunken overflow-hidden flex flex-col max-h-[80vh]"
      aria-label="MIDI Router and controller mapping"
    >
      <header className="shrink-0 flex items-center justify-between px-4 py-2 bg-surface-raised border-b border-brd-subtle">
        <div>
          <h3 className="text-sm font-semibold text-content-primary">
            MIDI wiring
          </h3>
          <p className="text-[11px] text-content-tertiary">
            Cable hardware inputs into the Sequencer destination, then map pads
            to your controller.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors"
          aria-label="Close MIDI panel"
        >
          <X size={14} />
        </button>
      </header>

      <div className="p-4 flex flex-col gap-4 overflow-y-auto min-h-0">
        <MidiRouter variant="modal" onRouteLimitReached={onUpgradeNeeded} />

        <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
                Controller mapping
              </h4>
              <p className="text-[10px] text-content-tertiary mt-0.5">
                Click <span className="font-mono">Learn</span>, then press the
                matching key or pad on your hardware controller.
              </p>
            </div>
            {Object.keys(padNoteBindings).length > 0 && (
              <button
                type="button"
                onClick={clearAllPadBindings}
                className="text-[10px] font-mono text-content-tertiary hover:text-destructive"
                title="Clear every pad binding"
              >
                Clear all
              </button>
            )}
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {Array.from({ length: 16 }).map((_, i) => {
              const pad = resolvedPads[i]
              const bound = padNoteBindings[i]
              const armed = midiLearnArmedPad === i
              // Chromatic-by-pad trigger default (C3 at pad 0). This
              // is the note the controller must send to fire the pad,
              // independent of the pad's MIDI-OUT note. Pads with no
              // content have no trigger — fall through to undefined.
              const chromaticDefault = pad ? 48 + i : undefined
              const effectiveNote = bound ?? chromaticDefault
              return (
                <li
                  key={i}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-[11px] font-mono transition-colors ${
                    armed
                      ? 'border-[color:var(--accent-halo)] bg-accent-muted animate-pulse'
                      : 'border-brd-subtle bg-surface-sunken'
                  }`}
                >
                  <span className="text-content-tertiary w-6 text-right">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-content-primary">
                    {pad?.label ?? (
                      <span className="italic text-content-tertiary">
                        empty
                      </span>
                    )}
                  </span>
                  <span
                    className="text-content-tertiary tabular-nums min-w-[3.5rem] text-right"
                    title={
                      bound !== undefined
                        ? 'Bound via MIDI Learn (overrides chromatic default)'
                        : 'Chromatic default — note that triggers this pad from your controller'
                    }
                  >
                    {effectiveNote !== undefined
                      ? `${midiNoteName(effectiveNote)} · ${effectiveNote}`
                      : '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onArmPad(armed ? null : i)}
                    className={`px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                      armed
                        ? 'border-accent bg-accent text-[color:var(--accent-ink)]'
                        : 'border-brd-subtle text-content-secondary hover:border-[color:var(--accent-halo)] hover:text-accent'
                    }`}
                    title={
                      armed
                        ? 'Cancel — press again to stop listening'
                        : 'Bind the next incoming MIDI note to this pad'
                    }
                  >
                    {armed ? 'Listening…' : 'Learn'}
                  </button>
                  {bound !== undefined && !armed && (
                    <button
                      type="button"
                      onClick={() => clearPadNote(i)}
                      className="px-1.5 py-0.5 rounded text-[10px] text-content-tertiary hover:text-destructive"
                      title={`Clear binding for pad ${i + 1}`}
                      aria-label={`Clear binding for pad ${i + 1}`}
                    >
                      ✕
                    </button>
                  )}
                </li>
              )
            })}
          </ul>

          <p className="text-[10px] text-content-tertiary mt-2">
            Bound notes are stored per-controller-layout (global) — learn once,
            works across every project.{' '}
            {project.midiPads.length > 0 && (
              <>
                Overflow MIDI pads below the grid match by note + channel and
                aren&apos;t affected.
              </>
            )}
          </p>
        </section>

        {/* App-wide control mappings: any mappable UI control
            (Play, BPM, FX engage, pad triggers, future audio bus
            faders) can be driven from this panel's Learn mode. */}
        <MappingList />
      </div>
    </section>
  )
}
